<template>
    <v-container class="pa-5 d-flex flex-column" fluid style="height: 100%">
        <JobParametersDialog
            :is-open="jobParametersDialog"
            :job-data="job"
            :is-job-editable="isJobEditable"
            @on-save="saveJobParameters"
            @on-close="closeJobParametersDialog"
        />

        <ImpositionOrdinalNumbersDialog
            :is-open="impositionOrdinalNumbersDialog"
            :job-data="job"
            @on-save="saveJobParameters"
            @on-close="closeImpositionOrdinalNumbersDialog"
        />

        <ImpositionParametersDialog
            :is-open="impositionParametersDialog"
            :job-id="job.id"
            :job-status-id="job.statusId"
            :imposition-data="currentImposition"
            :task-data="currentTask"
            :papers="papers"
            :job-colour-templates="jobColourTemplates"
            :machines="machines"
            :is-job-editable="isJobEditable"
            :elastic-materials="elasticMaterials"
            @show-color-settings-dialog="showColorSettingsDialog"
            @close-color-settings-dialog="closeColorSettingsDialog"
            @on-save="saveJobParameters"
            @on-close="closeImpositionParametersDialog"
        />

        <ColorSettingsDialog
            :is-open="colorSettingsDialog"
            :imposition-data="currentImposition"
            :side-data="currentColorSettingsSide"
            :job-print-colors="jobPrintColors"
            :task-data="currentTask"
            @on-save="saveColorSettings"
            @on-close="closeColorSettingsDialog"
        />

        <TaskParametersDialog
            :total-delivery-weight="job.totalDeliveryWeight"
            :job-id="job.id"
            :job-status-id="job.statusId"
            :imposition-data="currentImposition"
            :task-data="currentTask"
            :plate-types-data="plateTypes"
            :machines="machines"
            :papers="papers"
            :is-open="taskParametersDialog"
            :is-job-editable="isJobEditable"
            :action-loading="actionLoading.materials"
            @show-materials-dialog="showMaterialsDialog"
            @close-materials-dialog="closeMaterialsDialog"
            @show-slicing-formats-dialog="showSlicingFormatsDialog"
            @on-save="saveJobParameters"
            @on-close="closeTaskParametersDialog"
        />

        <MaterialsDialog
            :is-open="materialsDialog"
            is-editable
            :task-data="currentTask"
            :task-materials-data="currentTask.impositionTaskMaterial"
            :available-materials="materials"
            @on-save="saveMaterials"
            @on-close="closeMaterialsDialog"
        />

        <SlicingFormatsDialog
            :is-open="slicingFormatsDialog"
            :task-data="currentTask"
            :imposition-task-order-component-data="currentImpositionTaskOrderComponent"
            @on-save="saveSlicingFormats"
            @on-close="closeSlicingFormatsDialog"
        />

        <ProcessesAvailableToAddDialog
            :is-open="processesAvailableToAddDialog"
            :task-data="currentTask"
            :processes-data="currentProcessesAvailableToAdd"
            :action-loading="actionLoading.addTask"
            @on-save="addTask"
            @on-close="closeProcessesAvailableToAddDialog"
        />

        <ShipmentsOrdersDialog
            :is-open="shippingOrdersDialog"
            :shipping-orders="job.shippingOrders"
            @show-shipping-order="showShipmentsOrder"
            @on-close="closeShipmentsOrdersDialog"
        />

        <ConfirmDialog
            :is-open="confirmDeleteTaskDialog"
            :action-loading="actionLoading.deleteTask"
            @on-confirm="deleteTask"
            @on-abort="closeConfirmDeleteTaskDialog"
        >
            <template #title>
                Usuwanie procesu <strong v-if="currentTask.process">"{{ currentTask.process.name }}"</strong>
            </template>
            <template #content>
                Czy na pewno chcesz usunąć proces?
            </template>
            <template #confirmButton>
                Tak
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmForwardJobDialog"
            @on-confirm="forwardJob"
            @on-abort="closeConfirmForwardJobDialog"
        >
            <template #title>
                Przekazanie zlecenia do technologa
            </template>
            <template #content>
                Czy na pewno chcesz przekazać zlecenie do technologa?
            </template>
            <template #confirmButton>
                Tak
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmCloseJobDialog"
            @on-confirm="closeJob"
            @on-abort="closeConfirmCloseJobDialog"
        >
            <template #title>
                Zamykanie zlecenia
            </template>
            <template #content>
                Czy na pewno chcesz zamknąć zlecenie i przekazać je do planu?
            </template>
            <template #confirmButton>
                Zamknij
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmReopenJobDialog"
            @on-confirm="reopenJob"
            @on-abort="closeConfirmReopenJobDialog"
        >
            <template #title>
                Otwieranie zlecenia
            </template>
            <template #content>
                Czy na pewno chcesz otworzyć zlecenie?
            </template>
            <template #confirmButton>
                Otwórz
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmCancelJobDialog"
            @on-confirm="cancelJob"
            @on-abort="closeConfirmCancelJobDialog"
        >
            <template #title>
                Anulowanie zlecenia
            </template>
            <template #content>
                Czy na pewno chcesz anulować zlecenie?
            </template>
            <template #confirmButton>
                Potwierdź
            </template>
        </ConfirmDialog>

        <ReprintJobDialog
            :job-id="job.id"
            :job-processes="job.jobProcesses"
            :order-products="job.orderProduct"
            :is-open="reprintJobDialog"
            :action-loading="actionLoading.reprintJob"
            @on-confirm="reprintJob"
            @on-close="closeReprintJobDialog"
        />

        <PrintReportsDialog
            :is-open="printReportsDialog"
            :job-id="job.id"
            :action-loading="actionLoading.printReports"
            @on-confirm="printReports"
            @on-abort="closePrintReportsDialog"
        />

        <ManageComponentsDialog
            :is-open="manageComponentsDialog"
            :product-components-data="job.orderProduct"
            :impositions-data="job.jobImposition"
            :action-loading="actionLoading.manageComponents"
            @on-save="manageComponents"
            @on-close="closeManageComponentsDialog"
        />

        <page-title>
            Zlecenie {{ job.number }}
            <template v-if="isJobReprinted">
                (dodruk)
            </template>
            <span :class="jobStatusClass" class="text-uppercase text-body-2">
                {{ job.status.name }}
            </span>
        </page-title>


        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>

        <Diagram
            :job-status="job.status"
            :is-job-editable="isJobEditable"
            :is-job-reprinted="isJobReprinted"
            :impositions="impositions"
            :validations="$v"
            @get-current-imposition-and-task="getCurrentImpositionAndTask"
        />
    </v-container>
</template>

<script>
import store from '@/./store'

import { appConfig } from '@/config'

import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'

import JobParametersDialog from './JobParametersDialog'
import ImpositionOrdinalNumbersDialog from './ImpositionOrdinalNumbersDialog'
import ImpositionParametersDialog from './ImpositionParametersDialog'
import TaskParametersDialog from './TaskParametersDialog'
import MaterialsDialog from '@/components/common/MaterialsDialog'
import SlicingFormatsDialog from './SlicingFormatsDialog'
import ColorSettingsDialog from './ColorSettingsDialog'
import ReprintJobDialog from './ReprintJobDialog'
import ProcessesAvailableToAddDialog from './ProcessesAvailableToAddDialog'
import ShipmentsOrdersDialog from './ShipmentsOrdersDialog'
import Diagram from './Diagram/Index'
import ManageComponentsDialog from './ManageComponentsDialog'
import PrintReportsDialog from '@/views/Jobs/Common/PrintReportsDialog'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { EventBus } from '@/utils/eventBus'
import { hasUserPermission } from '@/utils/permissionsHandler'
import { openPdfFile } from '@/utils/pdfHandler'

import { required, helpers, integer, decimal, alpha, minValue } from 'vuelidate/lib/validators'

export default {
    name: 'Job',
    components: {
        PageTitle,
        ConfirmDialog,
        MaterialsDialog,
        SlicingFormatsDialog,
        JobParametersDialog,
        ImpositionOrdinalNumbersDialog,
        ImpositionParametersDialog,
        TaskParametersDialog,
        ColorSettingsDialog,
        ReprintJobDialog,
        ProcessesAvailableToAddDialog,
        ShipmentsOrdersDialog,
        Diagram,
        PrintReportsDialog,
        ManageComponentsDialog
    },
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('jobs/getJob', to.params.jobId),
            store.dispatch('jobs/getJobPrintColors'),
            store.dispatch('jobs/getJobColourTemplates'),
            store.dispatch('configurations/getMachines', {
                paginate: '',
                shouldSavedInStore: false
            }),
            store.dispatch('configurations/getPapers', {
                paginate: '',
                shouldSavedInStore: false
            }),
            store.dispatch('configurations/getMaterials', {
                query: '?isVisible=true&materialType_id=24',
                shouldSavedInStore: false
            }),
            store.dispatch('configurations/tools/getDieCutters'),
            store.dispatch('configurations/tools/getDieCuttersOwnershipTypes'),
            store.dispatch('configurations/getCertificates')
        ])
            .then(responses => {
                next(vm => {
                    vm.job = responses[0].data
                    vm.jobPrintColors = responses[1].data.results
                    vm.jobColourTemplates = responses[2].data.results
                    vm.machines = responses[3].data.results
                    vm.papers = responses[4].data.results
                    vm.elasticMaterials = responses[5].data.results
                })
            })
            .catch(error => {
                errorHandler(error)
                next(error)
            })
    },
    beforeRouteUpdate(to, from, next) {
        store.dispatch('jobs/getJob', to.params.jobId)
            .then(response => {
                this.job = response.data
                EventBus.$emit('parseDiagramSchema', {
                    impositions: this.job.jobImposition,
                    tasks: this.job.jobTask,
                })
                next()
            })
            .catch(error => {
                errorHandler(error)
                next(error)
            })
    },
    data() {
        return {
            job: {
                status: {
                    name: null
                }
            },
            currentImposition: {},
            currentTask: {},
            currentColorSettingsSide: null,
            currentImpositionTaskOrderComponent: {},
            currentProcessesAvailableToAdd: null,
            jobPrintColors: null,
            jobColourTemplates: null,
            machines: [],
            papers: [],
            materials: [],
            diagramViewType: 'job',
            currentImpositionIndex: 0,
            jobParametersDialog: false,
            impositionOrdinalNumbersDialog: false,
            impositionParametersDialog: false,
            taskParametersDialog: false,
            colorSettingsDialog: false,
            materialsDialog: false,
            slicingFormatsDialog: false,
            processesAvailableToAddDialog: false,
            shippingOrdersDialog: false,
            confirmDeleteTaskDialog: false,
            confirmForwardJobDialog: false,
            confirmCloseJobDialog: false,
            reprintJobDialog: false,
            confirmReopenJobDialog: false,
            confirmCancelJobDialog: false,
            printReportsDialog: false,
            actionLoading: {
                materials: false,
                reprintJob: false,
                addTask: false,
                deleteTask: false,
                printReports: false,
                manageComponents: false
            },
            elasticMaterials: [],
            plateTypes: [],
            manageComponentsDialog: false,
            autoCloseDone: false
        }
    },
    computed: {
        impositions() {
            return this.job.jobImposition ? this.job.jobImposition : null
        },
        jobStatusClass() {
            if (!this.job.status.id) return

            let jobStatusClass = ''

            switch (this.job.status.id) {
                case 10: // szkic
                    jobStatusClass = 'info--text'
                    break
                case 20: // w trakcie
                    jobStatusClass = 'warning--text'
                    break
                case 40: // zamknięte
                    jobStatusClass = 'success--text'
                    break
                case 50: // anulowane
                    jobStatusClass = 'error--text'
                    break
                default:
                    break
            }

            return jobStatusClass
        },
        isTraderUser() {
            return hasUserPermission('jobs_trader_update')
        },
        isTechnologistUser() {
            return hasUserPermission('jobs_update')
        },
        userWithAutoCloseSetup() {
            return hasUserPermission('jobs_trader_update_close_job')
        },
        isJobInProgress() {
            return this.job.status.id === 20
        },
        isJobClosed() {
            return this.job.status.id === 40
        },
        isJobCancelled() {
            return this.job.status.id === 50
        },
        isJobEditable() {
            return (this.isTraderUser && !this.isJobInProgress && !this.isJobCancelled) ||
                (this.isTechnologistUser && !this.isJobCancelled) ||
                (this.userWithAutoCloseSetup && this.isJobInProgress)
        },
        isJobReprinted() {
            return this.job.reprint
        },
        formatRangeValidations() {
            const result = []

            this.job.jobImposition.forEach(imposition => {
                if (imposition.impositionTask.length > 0) {
                    const printTask = imposition.impositionTask.find(task => {
                        return task.processId === appConfig.common.processes.printTaskId
                    })

                    if (printTask) {
                        const printMachineSheetRequirements = {
                            minSheetWidth: printTask.machine.minSheetWidth,
                            maxSheetWidth: printTask.machine.maxSheetWidth,
                            minSheetHeight: printTask.machine.minSheetHeight,
                            maxSheetHeight: printTask.machine.maxSheetHeight,
                        }

                        result.push({
                            impositionId: imposition.id,
                            formatARangeX: imposition.formatA >= printMachineSheetRequirements.minSheetWidth && imposition.formatA <= printMachineSheetRequirements.maxSheetWidth,
                            formatARangeY: imposition.formatA >= printMachineSheetRequirements.minSheetHeight && imposition.formatA <= printMachineSheetRequirements.maxSheetHeight,
                            formatBRangeX: imposition.formatB >= printMachineSheetRequirements.minSheetWidth && imposition.formatB <= printMachineSheetRequirements.maxSheetWidth,
                            formatBRangeY: imposition.formatB >= printMachineSheetRequirements.minSheetHeight && imposition.formatB <= printMachineSheetRequirements.maxSheetHeight
                        })
                    }
                }
            })

            return result
        }
    },
    watch: {
        job: {
            handler() {
                if (this.$route.params.autoClose && !this.autoCloseDone) {
                    this.autoCloseDone = true
                    
                    if (this.$v.$invalid) {
                        this.$store.dispatch('showNotification', {
                            type: 'error',
                            message: 'Proszę poprawnie rozpisać zlecenie.'
                        })

                        return
                    }
                    this.closeJob()
                }
            }
        }
    },
    validations() {
        const impositionFormatRangeHelper = () => {
            return helpers.withParams({ type: 'impositionFormatRangeHelper' }, (value, impositionModel) => {
                if (impositionModel.impositionTask.length > 0) {
                    const hasPrintTask = impositionModel.impositionTask.some(task => {
                        return task.processId === appConfig.common.processes.printTaskId
                    })

                    if (hasPrintTask) {
                        const formatRangeValidation = this.formatRangeValidations.find(item => item.impositionId === impositionModel.id)

                        const isValid = (formatRangeValidation.formatARangeX && formatRangeValidation.formatBRangeY) ||
                            (formatRangeValidation.formatARangeY && formatRangeValidation.formatBRangeX)

                        return isValid
                    } else {
                        return true
                    }
                } else {
                    return true
                }
            })
        }

        const taskPropertiesHelper = () => {
            return helpers.withParams({ type: 'taskPropertiesHelper' }, (propertyModel) => {

                const validators = {
                    required: required,
                    integer: integer,
                    alpha: alpha,
                    decimal: decimal,
                    greaterThanZero: minValue(0.01),
                }

                const requirements = propertyModel.productionProcessProperty.validators,
                    validations = []

                requirements.forEach(requirement => {
                    if (validators[requirement.name]) {
                        const isValid = validators[requirement.name](propertyModel.value)


                        validations.push({ requirement, isValid })
                    }
                })

                return validations.every(item => item.isValid)
            })
        }

        const rules = {
            job: {
                jobImposition: {
                    $each: {
                        formatA: {
                            isValid: impositionFormatRangeHelper()
                        },
                        formatB: {
                            isValid: impositionFormatRangeHelper()
                        },
                        impositionTask: {
                            $each: {
                                machineId: { required },
                                impositionTaskProperties: {
                                    $each: {
                                        isValid: taskPropertiesHelper()
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return rules
    },
    mounted() {
        EventBus.$emit('parseDiagramSchema', {
            impositions: this.job.jobImposition,
            tasks: this.job.jobTask,
        })
    },
    created() {
        EventBus.$on('changeDiagramView', viewType => { this.changeDiagramView(viewType) })
        EventBus.$on('changeImposition', index => { this.changeImposition(index) })
        EventBus.$on('showJobParametersDialog', () => { this.showJobParametersDialog() })
        EventBus.$on('showImpositionOrdinalNumbersDialog', () => { this.showImpositionOrdinalNumbersDialog() })
        EventBus.$on('showImpositionParametersDialog', () => { this.showImpositionParametersDialog() })
        EventBus.$on('showTaskParametersDialog', () => { this.showTaskParametersDialog() })
        EventBus.$on('showProcessesAvailableToAddDialog', data => { this.showProcessesAvailableToAddDialog(data) })
        EventBus.$on('showShipmentsOrdersDialog', () => { this.showShipmentsOrdersDialog() })
        EventBus.$on('showConfirmDeleteTaskDialog', () => { this.showConfirmDeleteTaskDialog() })
        EventBus.$on('showConfirmForwardJobDialog', () => { this.showConfirmForwardJobDialog() })
        EventBus.$on('showConfirmCloseJobDialog', () => { this.showConfirmCloseJobDialog() })
        EventBus.$on('showConfirmReopenJobDialog', () => { this.showConfirmReopenJobDialog() })
        EventBus.$on('showPrintReportsDialog', () => { this.showPrintReportsDialog() })
        EventBus.$on('showReprintJobDialog', () => { this.showReprintJobDialog() })
        EventBus.$on('showConfirmCancelJobDialog', () => { this.showConfirmCancelJobDialog() })
        EventBus.$on('showManageComponentsDialog', () => { this.showManageComponentsDialog() })
    },
    destroyed() {
        EventBus.$off()
    },
    methods: {
        changeDiagramView(viewType) {
            this.diagramViewType = viewType
            this.parseDiagramSchema()
            this.currentTask = {}
        },
        changeImposition(index) {
            this.currentImposition = this.job.jobImposition[index]
            this.currentImpositionIndex = index
            EventBus.$emit('parseDiagramSchema', {
                impositions: this.job.jobImposition,
                tasks: this.currentImposition.impositionTask
            })
        },
        showJobParametersDialog() {
            this.jobParametersDialog = true
            addSentryBreadcrumb('ui.action', 'Parametry zlecenia')
        },
        saveJobParameters(job) {
            this.job = job
            this.parseDiagramSchema()
        },
        closeJobParametersDialog() {
            this.jobParametersDialog = false
        },
        showImpositionOrdinalNumbersDialog() {
            this.impositionOrdinalNumbersDialog = true
            addSentryBreadcrumb('ui.action', 'Edycja zbiorcza numerów arkuszy')
        },
        closeImpositionOrdinalNumbersDialog() {
            this.impositionOrdinalNumbersDialog = false
        },
        showImpositionParametersDialog() {
            this.impositionParametersDialog = true
        },
        closeImpositionParametersDialog() {
            this.impositionParametersDialog = false

            setTimeout(() => {
                EventBus.$emit('getCurrentImpositionAndTask', this.currentTask.id)
            }, 500)
        },
        showTaskParametersDialog() {
            this.taskParametersDialog = true
            addSentryBreadcrumb('ui.action', 'Edycja/podgląd procesu')
        },
        closeTaskParametersDialog() {
            this.taskParametersDialog = false

            setTimeout(() => {
                EventBus.$emit('getCurrentImpositionAndTask', this.currentTask.id)
            }, 500)
        },
        showColorSettingsDialog({ imposition, side }) {
            this.currentImposition = imposition
            this.currentColorSettingsSide = side
            this.impositionParametersDialog = false

            setTimeout(() => {
                this.colorSettingsDialog = true
            }, 200)
        },
        closeColorSettingsDialog() {
            this.colorSettingsDialog = false

            setTimeout(() => {
                this.impositionParametersDialog = true
            }, 200)
        },
        saveColorSettings({ imposition, colorSettings, side}) {
            const colorKey = `impositionColours${side}`

            imposition[colorKey] = colorSettings
            
        },
        showMaterialsDialog() {
            this.actionLoading.materials = true

            this.$store.dispatch('configurations/getMaterials', {
                query: `?isVisible=true&productionProcess_id=${this.currentTask.processId}`,
                shouldSavedInStore: false
            })
                .then(response => {
                    this.materials = response.data.results
                    this.taskParametersDialog = false

                    setTimeout(() => {
                        this.materialsDialog = true
                    }, 200)
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.materials = false
                })
        },
        closeMaterialsDialog() {
            this.materialsDialog = false
            this.materials = []

            setTimeout(() => {
                this.taskParametersDialog = true
            }, 200)
        },
        saveMaterials(task, materials) {
            this.currentTask = JSON.parse(JSON.stringify(task))
            this.currentTask.impositionTaskMaterial = materials
        },
        showSlicingFormatsDialog(impositionTaskOrderComponent) {
            this.currentImpositionTaskOrderComponent = impositionTaskOrderComponent
            this.taskParametersDialog = false

            setTimeout(() => {
                this.slicingFormatsDialog = true
            }, 200)
        },
        closeSlicingFormatsDialog() {
            this.slicingFormatsDialog = false
            this.currentImpositionTaskOrderComponent = {}

            setTimeout(() => {
                this.taskParametersDialog = true
            }, 200)
        },
        saveSlicingFormats(orderProductComponent) {
            const index = this.currentTask.impositionTaskOrderComponents.findIndex(item => item.id === orderProductComponent.id)

            this.currentTask.impositionTaskOrderComponents.splice(index, 1, orderProductComponent)
            this.closeSlicingFormatsDialog()
        },
        showProcessesAvailableToAddDialog(payload) {
            addSentryBreadcrumb('ui.action', 'Dodawanie nowego procesu')

            this.$store.dispatch('jobs/getProcessesAvailableToAdd', payload)
                .then(response => {
                    this.currentProcessesAvailableToAdd = {
                        previousProcesses: response.data.previousProcesses,
                        nextProcesses: response.data.nextProcesses,
                    }

                    this.currentTask = {
                        ...this.currentTask,
                        placement: payload.placement
                    }

                    this.processesAvailableToAddDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeProcessesAvailableToAddDialog() {
            this.processesAvailableToAddDialog = false
            this.currentProcessesAvailableToAdd = null
        },
        addTask(payload) {
            this.actionLoading.addTask = true

            this.$store.dispatch('jobs/addTask', payload)
                .then(() => {
                    this.$store.dispatch('jobs/getJob', this.job.id)
                        .then(response => {
                            this.job = response.data
                            this.parseDiagramSchema()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.closeProcessesAvailableToAddDialog()
                            this.actionLoading.addTask = false
                        })
                })
                .catch(error => {
                    this.actionLoading.addTask = false
                    this.closeProcessesAvailableToAddDialog()
                    errorHandler(error)
                })
        },
        showShipmentsOrdersDialog() {
            if (this.job.shippingOrders.length === 1) {
                this.showShipmentsOrder(this.job.shippingOrders[0])
            } else {
                this.shippingOrdersDialog = true
            }
        },
        closeShipmentsOrdersDialog() {
            this.shippingOrdersDialog = false
        },
        showShipmentsOrder(shippingOrder) {
            this.$router.push({
                name: 'jobsJobShipmentsOrder',
                params: {
                    jobId: this.job.id,
                    shippingOrderId: shippingOrder.id
                }
            })
                .catch(error => errorHandler(error))
        },
        showConfirmDeleteTaskDialog() {
            if (this.isJobEditable) {
                const process = this.currentTask.process

                addSentryBreadcrumb('ui.action', 'Usuwanie procesu')

                if (process.isDeletable) {
                    this.confirmDeleteTaskDialog = true
                } else {
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: `Usunięcie procesu "${process.name}" zostało zablokowane.`
                    })
                }
            }
        },
        closeConfirmDeleteTaskDialog() {
            this.confirmDeleteTaskDialog = false
        },
        deleteTask() {
            this.actionLoading.deleteTask = true

            this.$store.dispatch('jobs/deleteTask', this.currentTask)
                .then(() => {
                    this.$store.dispatch('jobs/getJob', this.job.id)
                        .then(response => {
                            this.job = response.data
                            this.parseDiagramSchema()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.closeConfirmDeleteTaskDialog()
                            this.actionLoading.deleteTask = false
                        })
                })
                .catch(error => {
                    this.actionLoading.deleteTask = false
                    this.closeConfirmDeleteTaskDialog()
                    errorHandler(error)
                })
        },
        getCurrentImpositionAndTask({ imposition, task, plateTypes }) {
            this.currentImposition = JSON.parse(JSON.stringify(imposition))
            this.currentTask = JSON.parse(JSON.stringify(task))
            this.plateTypes = JSON.parse(JSON.stringify(plateTypes))
        },
        showConfirmForwardJobDialog() {
            this.confirmForwardJobDialog = true
        },
        closeConfirmForwardJobDialog() {
            this.confirmForwardJobDialog = false
        },
        showConfirmCloseJobDialog() {
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Proszę poprawnie rozpisać zlecenie.'
                })

                return
            }

            this.confirmCloseJobDialog = true
        },
        closeConfirmCloseJobDialog() {
            this.confirmCloseJobDialog = false
        },
        showConfirmReopenJobDialog() {
            this.confirmReopenJobDialog = true
        },
        closeConfirmReopenJobDialog() {
            this.confirmReopenJobDialog = false
        },
        showConfirmCancelJobDialog() {
            this.confirmCancelJobDialog = true
        },
        closeConfirmCancelJobDialog() {
            this.confirmCancelJobDialog = false
        },
        reopenJob() {
            this.$store.dispatch('toggleCircularLoader', true)

            this.$store.dispatch('jobs/saveJob', {
                id: this.job.id,
                statusId: 20 // w trakcie
            })
                .then(response => {
                    this.job = response.data

                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Zlecenie zostało otwarte.'
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmReopenJobDialog()
                    this.$store.dispatch('toggleCircularLoader', false)
                })
        },
        showPrintReportsDialog() {
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Proszę poprawnie rozpisać zlecenie.'
                })

                return
            }

            this.printReportsDialog = true
        },
        closePrintReportsDialog() {
            this.printReportsDialog = false
        },
        printReports(data) {
            this.actionLoading.printReports = true
            this.$store.dispatch('jobs/printJobReports', data)
                .then(response => {
                    openPdfFile(response.data)
                    this.closePrintReportsDialog()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.printReports = false
                })
        },
        forwardJob() {
            this.$store.dispatch('toggleCircularLoader', true)
            this.$store.dispatch('jobs/saveJob', {
                id: this.job.id,
                statusId: 20 // w trakcie
            })
                .then(response => {
                    this.job = response.data
                    this.closeConfirmForwardJobDialog()

                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Zlecenie przekazane do technologa.'
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$store.dispatch('toggleCircularLoader', false)
                })
        },
        closeJob() {
            this.$store.dispatch('toggleCircularLoader', true)

            this.$store.dispatch('jobs/saveJob', {
                id: this.job.id,
                statusId: 40 // zamknięte
            })
                .then(response => {
                    this.job = response.data
                    this.parseDiagramSchema()

                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Zlecenie zostało zamknięte.'
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmCloseJobDialog()
                    this.$store.dispatch('toggleCircularLoader', false)
                })
        },
        cancelJob() {
            this.$store.dispatch('toggleCircularLoader', true)

            this.$store.dispatch('jobs/saveJob', {
                id: this.job.id,
                statusId: 50 // anulowane
            })
                .then(response => {
                    this.job = response.data

                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Zlecenie zostało anulowane.'
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmCancelJobDialog()
                    this.$store.dispatch('toggleCircularLoader', false)
                })
        },
        showReprintJobDialog() {
            this.reprintJobDialog = true
        },
        closeReprintJobDialog() {
            this.reprintJobDialog = false
        },
        reprintJob(payload) {
            this.actionLoading.reprintJob = true
            this.$store.dispatch('jobs/reprintJob', payload)
                .then(response => {
                    this.$router.push({
                        name: 'jobsJob',
                        params: {
                            jobId: response.data.createdJobId
                        }
                    })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.actionLoading.reprintJob = false
                            this.closeReprintJobDialog()
                        })
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.reprintJob = false
                    this.closeReprintJobDialog()
                })
        },
        showManageComponentsDialog() {
            this.manageComponentsDialog = true
        },
        closeManageComponentsDialog() {
            this.manageComponentsDialog = false
        },
        manageComponents(payload) {
            this.actionLoading.manageComponents = true

            this.$store.dispatch('jobs/componentsToImpositions', {'componentsToImpositions': payload})
                .then(() => {
                    this.$store.dispatch('jobs/getJob', this.job.id)
                        .then(response => {
                            this.job = response.data
                            this.parseDiagramSchema()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.actionLoading.manageComponents = false
                            this.manageComponentsDialog = false
                        })
                })
                .catch(error => errorHandler(error))
        },
        parseDiagramSchema() {
            if (this.diagramViewType === 'job') {
                EventBus.$emit('parseDiagramSchema', {
                    impositions: this.job.jobImposition,
                    tasks: this.job.jobTask,
                })
            } else if (this.diagramViewType === 'imposition') {
                this.currentImposition = this.job.jobImposition[this.currentImpositionIndex]
                EventBus.$emit('parseDiagramSchema', {
                    impositions: this.job.jobImposition,
                    tasks: this.job.jobImposition[this.currentImpositionIndex].impositionTask
                })
            }
        },
        close() {
            this.$router.push({ name: 'jobs' })
                .catch(error => {
                    errorHandler(error)
                })
        },
    }
}

</script>
